import moment from 'moment-timezone'

/**
 * Mapping of deprecated IANA timezone names to their modern equivalents
 * Used to convert browser-detected deprecated timezones to valid ones
 */
const DEPRECATED_TIMEZONE_MAP = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Kashgar': 'Asia/Urumqi',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Chungking': 'Asia/Chongqing',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'America/Argentina/ComodRivadavia': 'America/Argentina/Catamarca',
  'America/Atka': 'America/Adak',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Catamarca': 'America/Argentina/Catamarca',
  'America/Cordoba': 'America/Argentina/Cordoba',
  'America/Ensenada': 'America/Tijuana',
  'America/Fort_Wayne': 'America/Indiana/Indianapolis',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'America/Jujuy': 'America/Argentina/Jujuy',
  'America/Knox_IN': 'America/Indiana/Knox',
  'America/Louisville': 'America/Kentucky/Louisville',
  'America/Mendoza': 'America/Argentina/Mendoza',
  'America/Montreal': 'America/Toronto',
  'America/Porto_Acre': 'America/Rio_Branco',
  'America/Rosario': 'America/Argentina/Cordoba',
  'America/Shiprock': 'America/Denver',
  'America/Virgin': 'America/Port_of_Spain',
  'Antarctica/South_Pole': 'Pacific/Auckland',
  'Asia/Chongqing': 'Asia/Shanghai',
  'Asia/Harbin': 'Asia/Shanghai',
  'Asia/Macao': 'Asia/Macau',
  'Asia/Tel_Aviv': 'Asia/Jerusalem',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Australia/ACT': 'Australia/Sydney',
  'Australia/Canberra': 'Australia/Sydney',
  'Australia/LHI': 'Australia/Lord_Howe',
  'Australia/NSW': 'Australia/Sydney',
  'Australia/North': 'Australia/Darwin',
  'Australia/Queensland': 'Australia/Brisbane',
  'Australia/South': 'Australia/Adelaide',
  'Australia/Tasmania': 'Australia/Hobart',
  'Australia/Victoria': 'Australia/Melbourne',
  'Australia/West': 'Australia/Perth',
  'Australia/Yancowinna': 'Australia/Broken_Hill',
  'Brazil/Acre': 'America/Rio_Branco',
  'Brazil/DeNoronha': 'America/Noronha',
  'Brazil/East': 'America/Sao_Paulo',
  'Brazil/West': 'America/Manaus',
  'Canada/Atlantic': 'America/Halifax',
  'Canada/Central': 'America/Winnipeg',
  'Canada/Eastern': 'America/Toronto',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Newfoundland': 'America/St_Johns',
  'Canada/Pacific': 'America/Vancouver',
  'Canada/Saskatchewan': 'America/Regina',
  'Canada/Yukon': 'America/Whitehorse',
  'Chile/Continental': 'America/Santiago',
  'Chile/EasterIsland': 'Pacific/Easter',
  Cuba: 'America/Havana',
  Egypt: 'Africa/Cairo',
  Eire: 'Europe/Dublin',
  'Europe/Belfast': 'Europe/London',
  'Europe/Tiraspol': 'Europe/Chisinau',
  GB: 'Europe/London',
  'GB-Eire': 'Europe/London',
  'GMT+0': 'Etc/GMT',
  'GMT-0': 'Etc/GMT',
  GMT0: 'Etc/GMT',
  Greenwich: 'Etc/GMT',
  Hongkong: 'Asia/Hong_Kong',
  Iceland: 'Atlantic/Reykjavik',
  Iran: 'Asia/Tehran',
  Israel: 'Asia/Jerusalem',
  Jamaica: 'America/Jamaica',
  Japan: 'Asia/Tokyo',
  Kwajalein: 'Pacific/Kwajalein',
  Libya: 'Africa/Tripoli',
  'Mexico/BajaNorte': 'America/Tijuana',
  'Mexico/BajaSur': 'America/Mazatlan',
  'Mexico/General': 'America/Mexico_City',
  NZ: 'Pacific/Auckland',
  'NZ-CHAT': 'Pacific/Chatham',
  Navajo: 'America/Denver',
  PRC: 'Asia/Shanghai',
  'Pacific/Johnston': 'Pacific/Honolulu',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Samoa': 'Pacific/Pago_Pago',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Pacific/Yap': 'Pacific/Chuuk',
  Poland: 'Europe/Warsaw',
  Portugal: 'Europe/Lisbon',
  ROC: 'Asia/Taipei',
  ROK: 'Asia/Seoul',
  Singapore: 'Asia/Singapore',
  Turkey: 'Europe/Istanbul',
  'US/Alaska': 'America/Anchorage',
  'US/Aleutian': 'America/Adak',
  'US/Arizona': 'America/Phoenix',
  'US/Central': 'America/Chicago',
  'US/East-Indiana': 'America/Indiana/Indianapolis',
  'US/Eastern': 'America/New_York',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Indiana-Starke': 'America/Indiana/Knox',
  'US/Michigan': 'America/Detroit',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Pacific-New': 'America/Los_Angeles',
  'US/Samoa': 'Pacific/Pago_Pago',
  'W-SU': 'Europe/Moscow',
}

/**
 * List of deprecated IANA timezone names that should be filtered out
 * These are legacy aliases that have been replaced by modern timezone names
 */
const DEPRECATED_TIMEZONES = new Set(Object.keys(DEPRECATED_TIMEZONE_MAP))

/**
 * Get all available IANA timezones from moment-timezone
 * Filters out deprecated timezone aliases to avoid duplicates
 * @returns {Array} Array of timezone objects with value and label
 */
export const getAllTimezones = () => {
  // Get all timezone names from moment-timezone
  const timezoneNames = moment.tz.names()

  // Filter out deprecated timezones
  const validTimezoneNames = timezoneNames.filter(
    (tzName) => !DEPRECATED_TIMEZONES.has(tzName),
  )

  // Create timezone options with GMT offset
  return validTimezoneNames.map((tzName) => {
    // Get the current offset for this timezone
    const offset = moment.tz(tzName).utcOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    const offsetString = `${sign}${String(hours).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`

    // Replace "Etc" with "ETC" in the display label
    const displayName = tzName.replace(/^Etc\//, 'ETC/')

    return {
      value: tzName,
      label: `${displayName} (GMT${offsetString})`,
    }
  })
}

/**
 * Get timezone options sorted by offset, then alphabetically
 * @returns {Array} Sorted array of timezone objects
 */
export const getSortedTimezones = () => {
  const timezones = getAllTimezones()

  return timezones.sort((a, b) => {
    // Extract offset from label
    const offsetA = extractOffset(a.label)
    const offsetB = extractOffset(b.label)

    // Sort by offset first (most negative to most positive)
    if (offsetA !== offsetB) {
      return offsetA - offsetB
    }

    // Then sort alphabetically by timezone name
    return a.value.localeCompare(b.value)
  })
}

/**
 * Extract GMT offset in minutes from timezone label
 * @param {string} label - Timezone label like "America/New_York (GMT-5:00)"
 * @returns {number} Offset in minutes
 */
const extractOffset = (label) => {
  const match = label.match(/GMT([+-])(\d{1,2}):(\d{2})/)
  if (!match) return 0

  const sign = match[1] === '+' ? 1 : -1
  const hours = parseInt(match[2], 10)
  const minutes = parseInt(match[3], 10)

  return sign * (hours * 60 + minutes)
}

/**
 * Get browser timezone with fallback and mapping to modern equivalents
 * Maps deprecated timezones to their modern equivalents
 * @returns {string} IANA timezone string (modern equivalent if deprecated)
 */
export const getBrowserTimezone = () => {
  try {
    if (
      typeof Intl !== 'undefined' &&
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      // If the browser timezone is deprecated, map it to the modern equivalent
      if (DEPRECATED_TIMEZONE_MAP[browserTimezone]) {
        return DEPRECATED_TIMEZONE_MAP[browserTimezone]
      }
      return browserTimezone
    }
  } catch (e) {
    // Ignore error
  }
  return ''
}

/**
 * Format timezone name for display
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted timezone with GMT offset
 */
export const formatTimezoneLabel = (timezone) => {
  if (!timezone) return ''

  try {
    const offset = moment.tz(timezone).utcOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    const offsetString = `${sign}${String(hours).padStart(2, '0')}:${String(
      minutes,
    ).padStart(2, '0')}`

    // Replace "Etc" with "ETC" in the display label
    const displayName = timezone.replace(/^Etc\//, 'ETC/')

    return `${displayName} (GMT${offsetString})`
  } catch (e) {
    return timezone
  }
}

/**
 * Get timezone offset in minutes for a given IANA timezone
 * @param {string} timezone - IANA timezone string
 * @returns {number|null} Offset in minutes or null on error
 */
export const getTimezoneOffsetMinutes = (timezone) => {
  if (!timezone) return null

  try {
    return moment.tz(timezone).utcOffset()
  } catch (e) {
    return null
  }
}
